import _get from 'lodash/get';
import _findIndex from 'lodash/findIndex';
import _compact from 'lodash/compact';
import {
  DEFAULT_MEETING_DURATION,
  formatInviteMessageBodyForWeb,
  NEW_LINE, OUTLOOK_NEW_LINE,
  outlookNewlineJoin,
  WEB_NEW_LINE
} from 'utils/officeJS/outlookUtils';
import Logger from 'js-logger';
import { getCallLink } from 'utils/inviteUtils';
import { NO_OFFICE } from 'utils/constants/constants';

const lifesizeLocationTemplate = 'https://call.lifesizecloud';
const clickToCallSuffix = 'Lifesize'; // doesn't require localization, which for a heuristic could get ugly

enum ItemOperation {
  SET = 'set',
  GET = 'get'
}
enum ItemProperty {
  BODY = 'body',
  LOCATION = 'location',
  SUBJECT = 'subject'
}

function closeTaskPane() {
  const taskPane = _get(Office, 'context.ui');
  setTimeout(() => {
    taskPane.closeContainer();
  }, 250);
}

// https://docs.microsoft.com/en-us/office/dev/add-ins/reference/objectmodel/requirement-set-1.5/office.context.mailbox.item
/*  displayReplyForm exists only in MessageReadMode */
const isReadMode = () => Office.context.mailbox.item.displayReplyForm !== undefined;

function createOrAppendItem(invitation: any, extension: string, isOfficeForWeb: boolean, isOfficeForPC: boolean) {
  if (NO_OFFICE) {
    // plugin is launched outside of outlook.resolve the promise.
    return;
  }
  const payload = Object.assign({}, invitation);
  if (!payload) {
    Logger.error('Missing argument: payload');
  }
  const itemType = Office.context.mailbox.item.itemType as Office.MailboxEnums.ItemType;
  const isAppointment = itemType === Office.MailboxEnums.ItemType.Appointment;
  const isMessage = itemType === Office.MailboxEnums.ItemType.Message;

  if (isAppointment || (!isReadMode() && isMessage)) {
    payload.location = getCallLink(extension);
    updateItem(payload, isOfficeForWeb, itemType, isOfficeForPC)
      .then(() => {
        closeTaskPane();
      })
      .catch((e) => {
        Logger.error(e);
      });
  } else {
    // called from an email message, so create a NEW appointment
    try {
      if (isMessage) {
        const mailBoxItem = Office.context.mailbox.item;
        payload.toRecipients = [...mailBoxItem.to, mailBoxItem.from];
        payload.ccRecipients = mailBoxItem.cc;
      }
      createAppointment(payload, extension, isOfficeForWeb, isOfficeForPC);
      closeTaskPane();
    } catch (e) {
      Logger.error(`Could not display the outlook new appointment form: ${e}`);
    }
  }
}

/**
 * Update Subject, Body, Location of an Office Item(Message and Mail)
 * @param payload
 * @param isOfficeForWeb
 * @param itemType Office.MailboxEnums.ItemType
 */
async function updateItem(payload: any, isOfficeForWeb: boolean, itemType: Office.MailboxEnums.ItemType, isOfficeForPC: boolean) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    const timeout = setTimeout(() => {
      resolve();
    }, 10000);

    const { location, subject, message, body } = payload;
    let updatedBody = body;
    const isScheduleMeeting = true;

    /** @type {Office.Appointment} */
    const item = Office.context.mailbox.item;
    const isAppointment = Office.MailboxEnums.ItemType.Appointment === itemType;
    try {
      const [originalBodyText, originalLocations, originalSubject] = await Promise.all([
        getBody(item, Office.CoercionType.Html),
        isAppointment ? getLocation(item) : Promise.resolve(''), // Message does not have location.
        getSubject(item)
      ]);

      const modifiedLocations = updateLocations(originalLocations as string, location).join(';');
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      updatedBody = updateBody(
        updateCallMeUrlInBody(updatedBody, location, isOfficeForWeb, isOfficeForPC),
        originalBodyText,
        message,
        isOfficeForWeb
      );
      await Promise.all([
        setBody(updatedBody, item, message, isOfficeForWeb, isScheduleMeeting),
        setSubject(originalSubject as string, subject, item),
        isAppointment ? setLocation(modifiedLocations, item) : Promise.resolve('') // Message does not have location.
      ]);
      clearTimeout(timeout);
      resolve();
    } catch (e) {
      clearTimeout(timeout);
      reject(JSON.stringify(e));
    }
  });
}
const updateCallMeUrlInBody = (body: string, callMeUrl: string, isOfficeForWeb: boolean, isOfficeForPC: boolean) => {
  if (!isOfficeForWeb || isOfficeForPC) {
    return body;
  }

  return body.replace(callMeUrl, `<a id="LPNoLP" href="${callMeUrl}">${callMeUrl}</a>`);
};

const createAppointment = (payload: any, extension: string, isOfficeForWeb: boolean, isOfficeForPC: boolean) => {
  const callMeUrl = getCallLink(extension);
  const body = updateCallMeUrlInBody(_get(payload, 'body', ''), callMeUrl, isOfficeForWeb, isOfficeForPC);
  const startTime = new Date();
  const endTime = new Date();
  endTime.setMinutes(startTime.getMinutes() + DEFAULT_MEETING_DURATION);
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  Office.context.mailbox.displayNewAppointmentForm({
    body: isOfficeForPC ? body.replace('\n', OUTLOOK_NEW_LINE) : formatInviteMessageBodyForWeb(body),
    location: callMeUrl,
    subject: payload.subject,
    start: startTime,
    end: endTime,
    requiredAttendees: payload.toRecipients || [],
    optionalAttendees: payload.ccRecipients || []
  });
};

const updateLocations = (locationsString = '', location: string) => {
  // split string and remove trailing semicolon, if any.
  const updatedLocations: string[] = _compact(locationsString.split(';'));
  // best guess
  // find out Lifesize invitation index, if there are multiple locations.
  const lzInviteIndex = _findIndex(updatedLocations, (meetingLocation: string) =>
    meetingLocation.toString().trim().startsWith(lifesizeLocationTemplate)
  );
  // if Lifesize invitation exists in Multiple location, replace it with the new invitation URL.
  if (lzInviteIndex !== -1) {
    updatedLocations[lzInviteIndex] = ` ${location}`;
  } else {
    updatedLocations.push(` ${location}`);
  }
  return updatedLocations;
};

const updateBody = (body: string, originalBodyText: any, message: string, isOfficeForWeb: boolean) => {
  let updatedBody = body;
  try {
    if (isOfficeForWeb) {
      updatedBody = body.replace(new RegExp(NEW_LINE, 'g'), WEB_NEW_LINE);
    }

    if (originalBodyText && originalBodyText.trim().length) {
      // empty body has one space in it (!!)
      // meeting has been EDITED
      // we've ADDED Lifesize data to a non-Lifesize invite, OR we haven't changed rooms
      // replace new Line only for Outlook OWA.
      updatedBody = isOfficeForWeb
        ? outlookNewlineJoin(isOfficeForWeb, message, updatedBody, originalBodyText)
        : updatedBody;
    } else {
      // replace new Line only for Outlook OWA.
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      updatedBody = isOfficeForWeb ? outlookNewlineJoin(isOfficeForWeb, message, updatedBody) : updatedBody;
    }
    return updatedBody;
  } catch (e) {
    Logger.error(`Failed to update the appointment body: ${e}`);
    return updatedBody;
  }
};

/**
 * Callback to process asyncResult returned by setting or getting Item property.
 * @param {Office.AsyncResult<String> | Office.AsyncResult<void>} asyncResult
 * @param {Function} promiseResolve
 * @param {Function} promiseReject
 * @param {ItemProperty} property
 * @param {ItemOperation} operation
 */
const processAsyncResult = (
  asyncResult: Office.AsyncResult<string> | Office.AsyncResult<void>,
  promiseResolve: Function,
  promiseReject: Function,
  property: ItemProperty,
  operation: ItemOperation
) => {
  if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
    Logger.error(`Failed to ${operation} ${property}`);
    return promiseReject(asyncResult.error);
  } else {
    Logger.log(`Successfully ${operation} ${property}`);
    return asyncResult.value !== null ? promiseResolve(asyncResult.value) : promiseResolve();
  }
};

async function setBody(
  body: string,
  appointment: Office.AppointmentCompose,
  message: string,
  isOfficeForWeb: boolean,
  isScheduleMeeting: boolean
) {
  if (isOfficeForWeb) {
    // special case for online where line breaks in plain text don't work
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    return new Promise((resolve, reject) => {
      appointment.body.setAsync(
        body,
        { coercionType: Office.CoercionType.Html },
        (asyncResult: Office.AsyncResult<void>) =>
          processAsyncResult(asyncResult, resolve, reject, ItemProperty.BODY, ItemOperation.SET)
      );
    });
  }

  /* merge Lifesize Invitation and Meeting description added by customer without losing formatting. */
  const div = document.createElement('div');
  div.style.whiteSpace = 'pre';
  const addCustomMessage = isScheduleMeeting && message ? `${message || ''} \n \n` : '';
  div.innerText = `${addCustomMessage} ${body} `;
  return new Promise((resolve, reject) => {
    appointment.body.prependAsync(
      `${div.outerHTML}<br/>`,
      { coercionType: 'html' },
      (asyncResult: Office.AsyncResult<void>) =>
        processAsyncResult(asyncResult, resolve, reject, ItemProperty.BODY, ItemOperation.SET)
    );
  });
}

async function setLocation(originalLocations: any, appointment: any) {
  return new Promise((resolve, reject) => {
    appointment.location.setAsync(originalLocations, (asyncResult: Office.AsyncResult<void>) =>
      processAsyncResult(asyncResult, resolve, reject, ItemProperty.LOCATION, ItemOperation.SET)
    );
  });
}

async function setSubject(originalSubject: string, subject: string, appointment: Office.AppointmentCompose) {
  return new Promise((resolve, reject) => {
    if (!originalSubject || originalSubject.trim().length === 0 || originalSubject.endsWith(clickToCallSuffix)) {
      // best guess
      appointment.subject.setAsync(subject, (asyncResult: Office.AsyncResult<void>) =>
        processAsyncResult(asyncResult, resolve, reject, ItemProperty.SUBJECT, ItemOperation.SET)
      );
    } else {
      resolve();
    }
  });
}

async function getBody(item: any, coercionType = Office.CoercionType.Text) {
  return new Promise((resolve, reject) => {
    item.body.getAsync(coercionType, (asyncResult: Office.AsyncResult<string>) =>
      processAsyncResult(asyncResult, resolve, reject, ItemProperty.BODY, ItemOperation.GET)
    );
  });
}

async function getSubject(item: any) {
  return new Promise((resolve, reject) => {
    item.subject.getAsync((asyncResult: Office.AsyncResult<string>) =>
      processAsyncResult(asyncResult, resolve, reject, ItemProperty.SUBJECT, ItemOperation.GET)
    );
  });
}

async function getLocation(item: any) {
  return new Promise((resolve, reject) => {
    item.location.getAsync((asyncResult: Office.AsyncResult<string>) =>
      processAsyncResult(asyncResult, resolve, reject, ItemProperty.LOCATION, ItemOperation.GET)
    );
  });
}

export { createOrAppendItem, isReadMode };
